import { motion } from "framer-motion";
import { useState } from "react";
import { MapPin, Factory, Warehouse } from "lucide-react";

const GlobalPresenceSection = () => {
  const [activeLocation, setActiveLocation] = useState<string | null>(null);

  // Coordonnées en pourcentage (0-100) basées sur la carte monde
  // x: 0 = Pacifique Ouest, 50 = Europe, 100 = Pacifique Est
  // y: 0 = Pôle Nord, 50 = Équateur, 100 = Pôle Sud
  const locations = [
    { name: "Moldavie", type: "factory", x: 57, y: 32, country: "Europe de l'Est" },
    { name: "Serbie", type: "factory", x: 55, y: 34, country: "Balkans" },
    { name: "Vietnam", type: "factory", x: 78, y: 48, country: "Asie du Sud-Est" },
    { name: "France", type: "factory", x: 51, y: 33, country: "Europe de l'Ouest" },
    { name: "Portugal", type: "factory", x: 47, y: 36, country: "Europe de l'Ouest" },
    { name: "Turquie", type: "factory", x: 59, y: 36, country: "Europe/Asie" },
    { name: "Tunisie", type: "factory", x: 53, y: 42, country: "Afrique du Nord" },
    { name: "Espagne", type: "factory", x: 49, y: 37, country: "Europe de l'Ouest" },
    { name: "Chine", type: "factory", x: 78, y: 38, country: "Asie de l'Est" },
    { name: "Angleterre", type: "factory", x: 50, y: 28, country: "Europe de l'Ouest" },
    { name: "Bordeaux", type: "warehouse", x: 50, y: 34, note: "Siège social" },
    { name: "Toulouse", type: "warehouse", x: 51, y: 35, note: "" },
    { name: "Paris", type: "warehouse", x: 51, y: 31, note: "" },
  ];

  const headquarters = { x: 50, y: 34 };

  return (
    <section className="py-24 bg-gradient-to-b from-background via-primary/5 to-background overflow-hidden">
      <div className="container mx-auto px-4">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-display font-bold leading-tight tracking-wide text-foreground">
            NOUS TRAVAILLONS{" "}
            <span className="text-gradient-blue">DANS LE MONDE</span>{" "}
            ENTIER
          </h2>
          <p className="mt-6 text-lg text-muted-foreground max-w-2xl mx-auto">
            Un réseau international de production et de distribution pour vous garantir qualité et réactivité
          </p>
        </motion.div>

        {/* Map Container */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          whileInView={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1, delay: 0.2 }}
          viewport={{ once: true }}
          className="relative max-w-6xl mx-auto"
        >
          {/* Glow Effects */}
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-primary/10 rounded-full blur-[120px]" />
          <div className="absolute top-1/3 right-1/4 w-[300px] h-[300px] bg-eco/10 rounded-full blur-[80px]" />

          {/* Map Card */}
          <div className="relative glass-card rounded-3xl p-8 md:p-12 border border-primary/10 shadow-float">
            {/* Real World Map Container */}
            <div className="relative w-full h-[350px] md:h-[450px] bg-gradient-to-b from-primary/5 to-background rounded-2xl overflow-hidden">
              {/* World Map Image - Style blanc/bleu */}
              <img 
                src="https://upload.wikimedia.org/wikipedia/commons/thumb/e/ec/World_map_blank_without_borders.svg/2000px-World_map_blank_without_borders.svg.png"
                alt="Carte du monde"
                className="absolute inset-0 w-full h-full object-contain"
                style={{ filter: 'brightness(0) saturate(100%) invert(40%) sepia(80%) saturate(400%) hue-rotate(180deg) brightness(95%)' }}
              />
              
            </div>
            {/* Legend & Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-8 pt-8 border-t border-primary/10">
              {/* Factories */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.1 }}
                viewport={{ once: true }}
                className="text-center"
              >
                <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-primary/10 mb-3">
                  <Factory className="w-6 h-6 text-primary" />
                </div>
                <div className="text-3xl font-display font-bold text-primary">10</div>
                <div className="text-sm text-muted-foreground">Usines</div>
              </motion.div>

              {/* Warehouses */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.2 }}
                viewport={{ once: true }}
                className="text-center"
              >
                <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-eco/10 mb-3">
                  <Warehouse className="w-6 h-6 text-eco" />
                </div>
                <div className="text-3xl font-display font-bold text-eco">3</div>
                <div className="text-sm text-muted-foreground">Entrepôts</div>
              </motion.div>

              {/* Countries */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.3 }}
                viewport={{ once: true }}
                className="text-center"
              >
                <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-primary/10 mb-3">
                  <MapPin className="w-6 h-6 text-primary" />
                </div>
                <div className="text-3xl font-display font-bold text-primary">11</div>
                <div className="text-sm text-muted-foreground">Pays</div>
              </motion.div>

              {/* Continents */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.4 }}
                viewport={{ once: true }}
                className="text-center"
              >
                <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-eco/10 mb-3">
                  <svg className="w-6 h-6 text-eco" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10" />
                    <path d="M2 12h20M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
                  </svg>
                </div>
                <div className="text-3xl font-display font-bold text-eco">4</div>
                <div className="text-sm text-muted-foreground">Continents</div>
              </motion.div>
            </div>

            {/* Location Lists */}
            <div className="grid md:grid-cols-2 gap-8 mt-8 pt-8 border-t border-primary/10">
              {/* Factories List */}
              <div>
                <h3 className="flex items-center gap-2 text-lg font-semibold mb-4 text-primary">
                  <Factory className="w-5 h-5" />
                  Nos Usines
                </h3>
                <div className="grid grid-cols-2 gap-3">
                  {locations.filter(l => l.type === "factory").map((location) => (
                    <motion.div
                      key={location.name}
                      className={`p-3 rounded-lg border transition-all cursor-pointer ${
                        activeLocation === location.name 
                          ? "border-primary bg-primary/10" 
                          : "border-primary/20 hover:border-primary/40 hover:bg-primary/5"
                      }`}
                      onMouseEnter={() => setActiveLocation(location.name)}
                      onMouseLeave={() => setActiveLocation(null)}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      <div className="font-medium text-foreground">{location.name}</div>
                      <div className="text-xs text-muted-foreground">{location.country}</div>
                    </motion.div>
                  ))}
                </div>
              </div>

              {/* Warehouses List - New Layout */}
              <div>
                <h3 className="flex items-center gap-2 text-lg font-semibold mb-4 text-eco">
                  <Warehouse className="w-5 h-5" />
                  Nos Entrepôts en France
                </h3>
                <div className="flex flex-col gap-4">
                  {/* Siège social - Bordeaux */}
                  {locations.filter(l => l.type === "warehouse" && l.note === "Siège social").map((location) => (
                    <motion.div
                      key={location.name}
                      className={`p-5 rounded-xl border-2 transition-all cursor-pointer relative overflow-hidden ${
                        activeLocation === location.name 
                          ? "border-eco bg-gradient-to-br from-eco/20 to-eco/5" 
                          : "border-eco/30 hover:border-eco/60 bg-gradient-to-br from-eco/10 to-transparent"
                      }`}
                      onMouseEnter={() => setActiveLocation(location.name)}
                      onMouseLeave={() => setActiveLocation(null)}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      <div className="absolute top-0 right-0 bg-eco text-white text-xs font-bold px-3 py-1 rounded-bl-lg">
                        Siège social
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-full bg-eco/20 flex items-center justify-center">
                          <MapPin className="w-6 h-6 text-eco" />
                        </div>
                        <div>
                          <div className="text-xl font-bold text-foreground">{location.name}</div>
                          <div className="text-sm text-muted-foreground">Centre de distribution principal</div>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                  
                  {/* Autres entrepôts */}
                  <div className="flex gap-3">
                    {locations.filter(l => l.type === "warehouse" && l.note !== "Siège social").map((location) => (
                      <motion.div
                        key={location.name}
                        className={`flex-1 p-4 rounded-lg border transition-all cursor-pointer text-center ${
                          activeLocation === location.name 
                            ? "border-eco bg-eco/10" 
                            : "border-eco/20 hover:border-eco/40 hover:bg-eco/5"
                        }`}
                        onMouseEnter={() => setActiveLocation(location.name)}
                        onMouseLeave={() => setActiveLocation(null)}
                        whileHover={{ scale: 1.03, y: -2 }}
                        whileTap={{ scale: 0.98 }}
                      >
                        <div className="w-10 h-10 rounded-full bg-eco/10 flex items-center justify-center mx-auto mb-2">
                          <Warehouse className="w-5 h-5 text-eco" />
                        </div>
                        <div className="font-semibold text-foreground">{location.name}</div>
                        <div className="text-xs text-muted-foreground mt-1">Entrepôt</div>
                      </motion.div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default GlobalPresenceSection;